import { z } from 'zod'

export enum CheckInRule {
  'Non-Cancellable' = 'Non-Cancellable',
  'Cancel' = 'Cancel',
}

/**
 * Highest cutoff a cancellation rule can use, in days before check-in.
 *
 * Mirrors `MAX_RULE_DAYS_BEFORE_CHECK_IN` in property-service's policy
 * validator (which rejects anything above it) and `MAX_POLICY_CUTOFF_DAYS` in
 * booking-service (how far ahead the pre-stay cron evaluates rules). A rule
 * past either bound is refused or never charged, so raise the three together.
 */
export const MAX_CHECKIN_RULE_DAYS = 90

/**
 * The two cutoffs referred to by name. Every other value is just its own number
 * of days — see {@link formatCheckInRuleDate} and `CheckInRuleDateOptions`.
 */
export const CheckInRuleDate = {
  'Day of Arrival': 0,
  'Day Before': 1,
} as const

/** English label for a cutoff, matching what the enum's reverse map produced. */
export function formatCheckInRuleDate(days: number): string {
  if (days === CheckInRuleDate['Day of Arrival']) return 'Day of Arrival'
  if (days === CheckInRuleDate['Day Before']) return 'Day Before'
  return `${days} Days Before`
}

export enum CheckInRulePenalty {
  'Value of first night' = 'Value of first night',
  'No Penalty' = 'No Penalty',
  'Percentage of booking total' = 'Percentage of booking total',
  'Fixed amount' = 'Fixed amount',
  'Entire Amount' = 'Entire Amount',
}

enum Times {
  '00:00:00' = '00:00:00',
  '00:30:00' = '00:30:00',
  '01:00:00' = '01:00:00',
  '01:30:00' = '01:30:00',
  '02:00:00' = '02:00:00',
  '02:30:00' = '02:30:00',
  '03:00:00' = '03:00:00',
  '03:30:00' = '03:30:00',
  '04:00:00' = '04:00:00',
  '04:30:00' = '04:30:00',
  '05:00:00' = '05:00:00',
  '05:30:00' = '05:30:00',
  '06:00:00' = '06:00:00',
  '06:30:00' = '06:30:00',
  '07:00:00' = '07:00:00',
  '07:30:00' = '07:30:00',
  '08:00:00' = '08:00:00',
  '08:30:00' = '08:30:00',
  '09:00:00' = '09:00:00',
  '09:30:00' = '09:30:00',
  '10:00:00' = '10:00:00',
  '10:30:00' = '10:30:00',
  '11:00:00' = '11:00:00',
  '11:30:00' = '11:30:00',
  '12:00:00' = '12:00:00',
  '12:30:00' = '12:30:00',
  '13:00:00' = '13:00:00',
  '13:30:00' = '13:30:00',
  '14:00:00' = '14:00:00',
  '14:30:00' = '14:30:00',
  '15:00:00' = '15:00:00',
  '15:30:00' = '15:30:00',
  '16:00:00' = '16:00:00',
  '16:30:00' = '16:30:00',
  '17:00:00' = '17:00:00',
  '17:30:00' = '17:30:00',
  '18:00:00' = '18:00:00',
  '18:30:00' = '18:30:00',
  '19:00:00' = '19:00:00',
  '19:30:00' = '19:30:00',
  '20:00:00' = '20:00:00',
  '20:30:00' = '20:30:00',
  '21:00:00' = '21:00:00',
  '21:30:00' = '21:30:00',
  '22:00:00' = '22:00:00',
  '22:30:00' = '22:30:00',
  '23:00:00' = '23:00:00',
  '23:30:00' = '23:30:00',
}

/** Penalties whose amount comes from the rule's `value` field. */
export function penaltyRequiresValue(
  penalty: CheckInRulePenalty | null | undefined
): boolean {
  return (
    penalty === CheckInRulePenalty['Fixed amount'] ||
    penalty === CheckInRulePenalty['Percentage of booking total']
  )
}

/**
 * Units the admin offers for a cooling-off window. The API stores a single
 * integer (`withinMinutesAfterBooking`) so "3 days" never has to be resolved
 * against a timezone; the unit is display-only.
 */
export const coolingOffUnits = ['minutes', 'hours', 'days'] as const
export type CoolingOffUnit = (typeof coolingOffUnits)[number]

const MINUTES_PER_COOLING_OFF_UNIT: Record<CoolingOffUnit, number> = {
  minutes: 1,
  hours: 60,
  days: 1440,
}

export function coolingOffToMinutes(
  amount: number,
  unit: CoolingOffUnit
): number {
  return amount * MINUTES_PER_COOLING_OFF_UNIT[unit]
}

/**
 * Inverse of {@link coolingOffToMinutes}: picks the largest unit that divides
 * the stored minutes exactly, so 4320 reads back as "3 days" and 90 as
 * "90 minutes" instead of a lossy "1.5 hours".
 */
export function minutesToCoolingOff(minutes: number): {
  amount: number
  unit: CoolingOffUnit
} {
  for (const unit of ['days', 'hours'] as const) {
    const factor = MINUTES_PER_COOLING_OFF_UNIT[unit]
    if (minutes > 0 && minutes % factor === 0) {
      return { amount: minutes / factor, unit }
    }
  }
  return { amount: minutes, unit: 'minutes' }
}

/**
 * Per-rule cooling-off: within N minutes after the booking was created, this
 * penalty governs instead of the rule's own. Mirrors the property-service
 * validator, which is the source of truth.
 */
export const ruleExceptionFormSchema = z
  .object({
    type: z.literal('cooling-off'),
    withinMinutesAfterBooking: z
      .number({ error: 'val.required' })
      .int()
      .min(1, { error: 'policies.coolingOff.durationMin' }),
    penalty: z.enum(CheckInRulePenalty),
    // CoolingOffFields writes an emptied input as null, not Number('') === 0,
    // so "Fixed amount" with no amount typed fails the check below instead of
    // silently saving $0.
    value: z.number().min(0).nullish(),
  })
  .refine((data) => !penaltyRequiresValue(data.penalty) || data.value != null, {
    message: 'val.required',
    path: ['value'],
  })
  // property-service defaults a missing value to 0 but rejects null, and a
  // value left over from a previous penalty choice would be misleading in the
  // stored JSON. Only penalties that use a value keep one.
  .transform((data) => {
    if (!penaltyRequiresValue(data.penalty)) data.value = 0
    return data
  })
export type RuleExceptionFormValues = z.infer<typeof ruleExceptionFormSchema>

export const policiesRulesFormSchema = z
  .object({
    date: z.number().int().min(0).max(MAX_CHECKIN_RULE_DAYS),
    time: z.enum(Times).nullish(),
    penalty: z.enum(CheckInRulePenalty).optional(),
    automaticApproval: z.boolean(),
    defaultRule: z.enum(CheckInRule),
    default: z.boolean().optional(),
    value: z.transform(Number).pipe(z.number()).nullish(),
    // nullish rather than nullable().default(null): a default makes the
    // schema's input type differ from its output, which react-hook-form's
    // typed resolver rejects. A missing exception is omitted from the JSON
    // and property-service stores it as null, so the result is the same.
    exception: ruleExceptionFormSchema.nullish(),
  })
  .refine(
    (data) => {
      if (data.defaultRule === CheckInRule.Cancel) {
        return data.penalty !== undefined && data.penalty !== null
      }
      return true
    },
    {
      message: 'val.required',
      path: ['penalty'],
    }
  )
  // By check-in day the booking is being honoured, so a regret window on the
  // "until arrival" rule makes no sense; property-service rejects it too.
  .refine(
    (data) =>
      data.date !== CheckInRuleDate['Day of Arrival'] || data.exception == null,
    {
      message: 'policies.coolingOff.notOnCheckInDay',
      path: ['exception'],
    }
  )
export type PoliciesRuleFormValues = z.infer<typeof policiesRulesFormSchema>

export const cancellationPoliciesFormSchema = z.object({
  name: z
    .string({ error: 'val.required' })
    .min(1, { error: 'val.required' }),
  setAsDefault: z.boolean(),
  description: z.string().max(255, { error: 'val.max255' }).optional(),
  rules: z.array(policiesRulesFormSchema),
})

export type CancellationPoliciesFormValues = z.infer<
  typeof cancellationPoliciesFormSchema
>
export type CancellationPoliciesFormInputValues = z.input<
  typeof cancellationPoliciesFormSchema
>

export const cancellationPoliciesSchema = cancellationPoliciesFormSchema.extend(
  {
    id: z.string(),
    propertyId: z.string(),
    rules: z.array(
      policiesRulesFormSchema.safeExtend({
        id: z.string(),
        cancellationPolicyId: z.string(),
      })
    ),
    rates: z.array(z.object({ id: z.string(), shortTitle: z.string() })),
  }
)

export const policiesRulesSchema = policiesRulesFormSchema.safeExtend({
  id: z.string(),
  cancellationPolicyId: z.string(),
})
export type CancellationPolicies = z.infer<typeof cancellationPoliciesSchema>

export type PoliciesRule = z.infer<typeof policiesRulesSchema>

export function getCheckInRuleDateOrder(date: number | string): number {
  if (typeof date === 'number') {
    return date
  }
  if (date === 'Day of Arrival') {
    return CheckInRuleDate['Day of Arrival']
  }
  if (date === 'Day Before') {
    return CheckInRuleDate['Day Before']
  }
  return Number.parseInt(date, 10) || 0
}
